import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Breadcrumbs, Link as MuiLink, Stack, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PainelFiltros from './relatorios/PainelFiltros';
import IndicadoresRelatorio from './relatorios/IndicadoresRelatorio';
import TabelaRelatorio from './relatorios/TabelaRelatorio';
import RelatorioModal from './RelatorioModal';
import { iconeDoRelatorio } from '../config/iconesRelatorio';
import api, { fetchPdfBlobUrl, downloadArquivo } from '../services/api';
import { getRelatorio } from '../config/relatorios';

const VAZIO = { dataInicio: '', dataFim: '', status: '', idPaciente: '', dataReferencia: '' };

/** Data de hoje em ISO local, para o nome do arquivo exportado. */
const hojeIso = () => {
    const d = new Date();
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

/**
 * Página de um relatório: filtros, indicadores e resultados em tabela.
 *
 * <b>Um recorte só.</b> `filtrosAplicados` é o que foi efetivamente consultado;
 * `valores` é o que está digitado no painel e ainda não foi aplicado. A tabela,
 * os indicadores, o PDF e o CSV leem todos de `filtrosAplicados` — é o que
 * garante que a exportação traga exatamente o que está na tela, e não o que o
 * usuário acabou de digitar sem aplicar.
 *
 * O PDF deixou de ser o conteúdo principal e virou ação secundária ("Visualizar
 * PDF", em modal): para conferir um número a tabela é mais rápida, e o PDF
 * continua à mão para imprimir ou anexar.
 */
export default function RelatorioViewer({ reportKey, colunas, extra, tituloPagina }) {
    const navigate = useNavigate();
    const cfg = useMemo(
        () => getRelatorio(reportKey) || { key: reportKey, titulo: reportKey, descricao: '' },
        [reportKey],
    );

    const [valores, setValores] = useState(VAZIO);
    const [filtrosAplicados, setFiltrosAplicados] = useState(VAZIO);
    const [mostrarErros, setMostrarErros] = useState(false);
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);

    const [exportando, setExportando] = useState({ pdf: false, csv: false });
    const [modal, setModal] = useState({ open: false, blobUrl: null, loading: false, error: null });
    const urlRef = useRef(null);

    // ── Filtros → query string ──────────────────────────────────────────────
    const paramsDe = useCallback((f) => {
        const p = {};
        // Subconjunto de colunas do relatório personalizável. Vai nas três
        // rotas (dados, pdf, csv) para a tabela, o PDF e o CSV mostrarem as
        // mesmas colunas.
        if (colunas && colunas.length) p.colunas = colunas.join(',');
        if (cfg.temDatas && f.dataInicio) p.dataInicio = f.dataInicio;
        if (cfg.temDatas && f.dataFim) p.dataFim = f.dataFim;
        if (cfg.temStatus && f.status) p.status = f.status;
        if (cfg.temPaciente && f.idPaciente) p.idPaciente = f.idPaciente;
        // Data única vai no parâmetro que o endpoint espera (dataFim para
        // "vencidos até", dataInicio para "a vencer a partir de").
        if (cfg.temDataReferencia && f.dataReferencia) {
            p[cfg.campoDataReferencia || 'dataFim'] = f.dataReferencia;
        }
        return p;
    }, [cfg, colunas]);

    const paramsAplicados = useMemo(
        () => paramsDe(filtrosAplicados),
        [paramsDe, filtrosAplicados],
    );

    const temFiltroAplicado = Object.keys(paramsAplicados).length > 0
        || Object.values(valores).some(Boolean);

    const invalido = (f) => {
        if (cfg.temPaciente && !f.idPaciente) return true;
        if (cfg.temDatas && f.dataInicio && f.dataFim && f.dataInicio > f.dataFim) return true;
        return false;
    };

    // ── Consulta ────────────────────────────────────────────────────────────
    const consultar = useCallback(async (f) => {
        setCarregando(true);
        setErro(null);
        try {
            const r = await api.get(`/relatorios/${cfg.key}/dados`, { params: paramsDe(f) });
            setDados(r.data);
        } catch (e) {
            // 400 vem com a mensagem do back-end; o resto é falha genérica.
            const msg = e?.response?.data?.message || e?.response?.data?.erro;
            setErro(msg || 'Não foi possível consultar o relatório. Tente novamente.');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, [cfg.key, paramsDe]);

    const aplicar = () => {
        setMostrarErros(true);
        if (invalido(valores)) return;
        setFiltrosAplicados(valores);
        consultar(valores);
    };

    const limpar = () => {
        setValores(VAZIO);
        setFiltrosAplicados(VAZIO);
        setMostrarErros(false);
        setPacienteSelecionado(null);
        if (!cfg.temPaciente) consultar(VAZIO);
        else setDados(null);
    };

    // Consulta ao abrir. Relatório de um paciente só espera a seleção: sem
    // idPaciente o endpoint devolveria 400.
    useEffect(() => {
        setValores(VAZIO);
        setFiltrosAplicados(VAZIO);
        setMostrarErros(false);
        setPacienteSelecionado(null);
        setDados(null);
        if (!cfg.temPaciente) consultar(VAZIO);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg.key]);

    // Trocar as colunas não muda o recorte: reconsulta mantendo o que já foi
    // aplicado, em vez de voltar o painel para o estado inicial.
    const colunasAplicadas = (colunas || []).join(',');
    useEffect(() => {
        if (!dados) return;
        consultar(filtrosAplicados);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colunasAplicadas]);

    /* A lista de pacientes não é mais baixada: o BuscaPaciente dentro do painel de
       filtros consulta o servidor conforme o usuário digita. */

    const revogar = () => {
        if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
    useEffect(() => revogar, []);

    // ── Exportação ──────────────────────────────────────────────────────────
    // Usa paramsAplicados, nunca os valores digitados: exportar tem de devolver
    // o que está na tela.
    const nomeArquivo = (ext) => `${cfg.key}-${hojeIso()}.${ext}`;

    const exportar = async (fmt) => {
        setExportando(s => ({ ...s, [fmt]: true }));
        try {
            await downloadArquivo(`/relatorios/${cfg.key}/${fmt}`, paramsAplicados, nomeArquivo(fmt));
        } catch {
            /* feedback via toast global */
        } finally {
            setExportando(s => ({ ...s, [fmt]: false }));
        }
    };

    const verPdf = async () => {
        setModal({ open: true, blobUrl: null, loading: true, error: null });
        try {
            const url = await fetchPdfBlobUrl(`/relatorios/${cfg.key}/pdf`, paramsAplicados);
            revogar();
            urlRef.current = url;
            setModal({ open: true, blobUrl: url, loading: false, error: null });
        } catch {
            setModal({ open: true, blobUrl: null, loading: false, error: 'Falha ao gerar o PDF.' });
        }
    };

    const fecharModal = () => {
        setModal(m => ({ ...m, open: false }));
        revogar();
    };

    // Sem consulta válida ainda, não há o que exportar.
    const semResultado = !dados || carregando || !!erro;

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Breadcrumbs sx={{ mb: 1 }} aria-label="Trilha de navegação">
                <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate('/relatorios')}
                    sx={{ font: 'inherit', border: 0, background: 'none', cursor: 'pointer' }}
                >
                    Relatórios
                </MuiLink>
                <Typography color="text.primary" variant="body2">{tituloPagina || cfg.titulo}</Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    flexWrap: 'wrap', mb: 2,
                }}
            >
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/relatorios')}
                    sx={{ mt: 0.25 }}
                >
                    Catálogo
                </Button>

                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 260, flexGrow: 1 }}>
                    <Box sx={{ mt: 0.5 }}>{iconeDoRelatorio(cfg.key)}</Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main', lineHeight: 1.3 }}>
                            {cfg.titulo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {cfg.descricao}
                        </Typography>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="contained" color="primary" disableElevation size="small"
                        startIcon={<PictureAsPdfIcon />}
                        loading={exportando.pdf}
                        disabled={semResultado}
                        onClick={() => exportar('pdf')}
                    >
                        Exportar PDF
                    </Button>
                    <Button
                        variant="outlined" color="primary" size="small"
                        startIcon={<GridOnIcon />}
                        loading={exportando.csv}
                        disabled={semResultado}
                        onClick={() => exportar('csv')}
                    >
                        Exportar CSV
                    </Button>
                    <Button
                        variant="text" color="inherit" size="small"
                        startIcon={<VisibilityOutlinedIcon />}
                        disabled={semResultado}
                        onClick={verPdf}
                    >
                        Visualizar PDF
                    </Button>
                </Stack>
            </Box>

            {extra}

            <PainelFiltros
                cfg={cfg}
                valores={valores}
                onChange={setValores}
                onAplicar={aplicar}
                onLimpar={limpar}
                pacienteSelecionado={pacienteSelecionado}
                onPacienteChange={setPacienteSelecionado}
                carregando={carregando}
                mostrarErros={mostrarErros}
                temFiltroAplicado={temFiltroAplicado}
            />

            {/* Recorte efetivamente consultado, como o PDF mostra no cabeçalho. */}
            {dados?.totais?.periodo && !carregando && (
                <Chip
                    size="small"
                    variant="outlined"
                    label={`${dados.totais.rotuloPeriodo || 'Período'}: ${dados.totais.periodo}`}
                    sx={{ mb: 2 }}
                />
            )}

            {!carregando && !erro && <IndicadoresRelatorio indicadores={dados?.indicadores} />}

            <TabelaRelatorio
                colunas={dados?.colunas ?? []}
                linhas={dados?.linhas ?? []}
                carregando={carregando}
                erro={erro}
                onTentarNovamente={() => consultar(filtrosAplicados)}
                semFiltroAinda={!dados}
                mensagemVazio={
                    cfg.temPaciente && !filtrosAplicados.idPaciente
                        ? 'Selecione um paciente e aplique os filtros para consultar.'
                        : 'Nenhum resultado para os filtros aplicados. Ajuste o período ou limpe os filtros.'
                }
            />

            <RelatorioModal
                open={modal.open}
                onClose={fecharModal}
                titulo={cfg.titulo}
                blobUrl={modal.blobUrl}
                nomeArquivo={nomeArquivo('pdf')}
                loading={modal.loading}
                error={modal.error}
            />
        </Box>
    );
}
